import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import api from '../services/api';
import Spinner from '../components/common/Spinner';

const BASE = '/api/v1/widget';

export default function ContactReply() {
  const [searchParams] = useSearchParams();
  const practiceToken = searchParams.get('practice');
  const threadId = searchParams.get('thread');

  const [step, setStep] = useState('identity'); // 'identity' | 'otp' | 'thread' | 'error'
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [otp, setOtp] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [widgetToken, setWidgetToken] = useState(null);
  const [thread, setThread] = useState(null);
  const [practiceName, setPracticeName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!practiceToken) {
    return <ErrorPage message="Invalid link. Please check the notification you received." />;
  }

  const requestOtp = async (e) => {
    e.preventDefault();
    if (!email && !phone) { setError('Please enter your email or phone number.'); return; }
    setError('');
    setLoading(true);
    try {
      // Fetch practice name for display
      const infoRes = await api.get(`${BASE}/${practiceToken}/info`);
      setPracticeName(infoRes.data.data.practiceName);

      const res = await api.post(`${BASE}/${practiceToken}/request-otp`, {
        email: email || undefined,
        phone: phone || undefined,
      });
      setSessionId(res.data.data.sessionId);
      setStep('otp');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!otp) { setError('Please enter the verification code.'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await api.post(`${BASE}/${practiceToken}/verify-otp`, { sessionId, otp });
      const token = res.data.data.widgetAccessToken;
      setWidgetToken(token);

      // Load the thread
      const threadRes = await api.get(`${BASE}/${practiceToken}/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setThread(threadRes.data.data.thread);
      setStep('thread');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-neutral-900">Secure Message</h1>
          {practiceName && <p className="text-neutral-500 mt-1">{practiceName}</p>}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
          {step === 'identity' && (
            <form onSubmit={requestOtp} className="space-y-4">
              <p className="text-sm text-neutral-600">
                Verify your identity to read your provider&apos;s reply. Enter the email or phone number you used when you first sent a message.
              </p>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Email address</label>
                <input
                  type="email"
                  className="input"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Or phone number</label>
                <input
                  type="tel"
                  className="input"
                  placeholder="+1 (206) 555-1234"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button type="submit" disabled={loading} className="btn btn-primary w-full">
                {loading ? <Spinner size="sm" /> : 'Send verification code'}
              </button>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={verifyOtp} className="space-y-4">
              <p className="text-sm text-neutral-600">
                We sent a 6-digit code to {email || phone}. Enter it below. The code expires in 10 minutes.
              </p>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Verification code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="input text-center text-2xl tracking-widest font-mono"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button type="submit" disabled={loading} className="btn btn-primary w-full">
                {loading ? <Spinner size="sm" /> : 'Verify'}
              </button>
              <button
                type="button"
                onClick={() => { setStep('identity'); setOtp(''); setError(''); }}
                className="w-full text-sm text-neutral-500 hover:text-neutral-700"
              >
                Use a different email or phone
              </button>
            </form>
          )}

          {step === 'thread' && thread && (
            <div>
              <h2 className="font-semibold text-neutral-900 mb-4">{thread.subject}</h2>
              <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
                {(thread.messages || []).map((m) => (
                  <div
                    key={m.id}
                    className={`rounded-lg p-3 text-sm ${
                      m.senderRole === 'client'
                        ? 'bg-neutral-100 text-neutral-700'
                        : 'bg-primary-50 text-primary-900 border border-primary-100'
                    }`}
                  >
                    <p className="font-medium text-xs mb-1 text-neutral-500">
                      {m.senderRole === 'client' ? 'You' : practiceName}
                      {' · '}
                      {m.createdAt ? format(parseISO(m.createdAt), 'MMM d, h:mm a') : ''}
                    </p>
                    <p className="whitespace-pre-wrap">{m.content}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-neutral-400 mt-4 text-center">
                To reply, please contact {practiceName} directly using the email or phone on file.
              </p>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-neutral-400 mt-4">
          This is a secure, HIPAA-compliant message thread.
        </p>
      </div>
    </div>
  );
}

function ErrorPage({ message }) {
  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <div className="text-center">
        <h1 className="text-xl font-semibold text-neutral-800 mb-2">Something went wrong</h1>
        <p className="text-neutral-500">{message}</p>
      </div>
    </div>
  );
}
